import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { ScreenQuadSpec, ScreenBox } from "../scenario/types";

const DEG = Math.PI / 180;

/**
 * GltfScreenOverlay — the ONE reusable "DOM screen glued to a 3D monitor"
 * primitive (rules ch.16). It renders NOTHING visible; each frame it takes a
 * world-space screen rectangle (`quad`) and projects its FOUR CORNERS with
 * `.project(camera)`, reporting the screen-space axis-aligned box (viewport
 * percent) up via `onBox`. App mounts the device DOM (e.g. MitmReveal) inside
 * that box, so it tracks the monitor at any window size/zoom — no more
 * pixel-measured, per-course vw/vh CSS (the recurring "6th time" hacker-screen
 * bug). Generalizes the projection pattern in DeskWorkstation/ConferenceTv.
 *
 * Calibrate a quad live (no build) with `?screenQuad=cx,cy,cz,w,h,yaw[,tilt]`
 * and read window.__screenBox in devtools.
 */
export function GltfScreenOverlay({
  quad,
  onBox,
}: {
  quad: ScreenQuadSpec;
  onBox: (box: ScreenBox) => void;
}) {
  const { camera } = useThree();
  const onBoxRef = useRef(onBox);
  onBoxRef.current = onBox;
  const last = useRef<ScreenBox>({
    leftPct: 0,
    topPct: 0,
    widthPct: 0,
    heightPct: 0,
    visible: false,
    distance: 999,
  });

  // Scratch vectors (avoid per-frame allocation).
  const center = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toScreen = useRef(new THREE.Vector3()).current;
  const corner = useRef(new THREE.Vector3()).current;
  const quatRef = useRef(new THREE.Quaternion()).current;
  const eulerRef = useRef(new THREE.Euler()).current;

  useFrame(() => {
    const yaw = (quad.rotationDeg ?? 0) * DEG;
    const tilt = (quad.tiltDeg ?? 0) * DEG;
    const hw = quad.width / 2;
    const hh = quad.height / 2;
    center.set(quad.center[0], quad.center[1], quad.center[2]);
    // Orientation: yaw about Y then tilt about X (screen faces +Z at yaw 0).
    eulerRef.set(tilt, yaw, 0, "YXZ");
    quatRef.setFromEuler(eulerRef);

    // Four local corners of the plane, rotated + translated to world, projected.
    const localCorners: [number, number][] = [
      [-hw, hh],
      [hw, hh],
      [hw, -hh],
      [-hw, -hh],
    ];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [lx, ly] of localCorners) {
      corner.set(lx, ly, 0).applyQuaternion(quatRef).add(center);
      corner.project(camera);
      const xPct = (corner.x * 0.5 + 0.5) * 100;
      const yPct = (-corner.y * 0.5 + 0.5) * 100;
      if (xPct < minX) minX = xPct;
      if (xPct > maxX) maxX = xPct;
      if (yPct < minY) minY = yPct;
      if (yPct > maxY) maxY = yPct;
    }

    camera.getWorldDirection(camDir);
    toScreen.copy(center).sub(camera.position);
    const distance = toScreen.length();
    const inFront = toScreen.normalize().dot(camDir) > 0.1;
    const maxDist = quad.maxDistance ?? 8;
    const onScreen = maxX > -10 && minX < 110 && maxY > -10 && minY < 110;

    const box: ScreenBox = {
      leftPct: minX,
      topPct: minY,
      widthPct: maxX - minX,
      heightPct: maxY - minY,
      visible: inFront && onScreen && distance < maxDist,
      distance,
    };

    const p = last.current;
    if (
      p.visible !== box.visible ||
      Math.abs(p.leftPct - box.leftPct) > 0.2 ||
      Math.abs(p.topPct - box.topPct) > 0.2 ||
      Math.abs(p.widthPct - box.widthPct) > 0.2 ||
      Math.abs(p.heightPct - box.heightPct) > 0.2
    ) {
      last.current = box;
      onBoxRef.current(box);
    }
    if (typeof window !== "undefined") {
      (window as unknown as { __screenBox?: ScreenBox }).__screenBox = box;
    }
  });

  return null;
}

/** Parse a `?screenQuad=cx,cy,cz,w,h,yaw[,tilt]` override (live calibration). */
export function parseScreenQuadOverride(search = window.location.search): ScreenQuadSpec | null {
  const raw = new URLSearchParams(search).get("screenQuad");
  if (!raw) return null;
  const n = raw.split(",").map((s) => Number(s.trim()));
  if (n.length < 5 || n.some((x) => Number.isNaN(x))) return null;
  return {
    center: [n[0], n[1], n[2]],
    width: n[3],
    height: n[4],
    rotationDeg: n[5] ?? 0,
    tiltDeg: n[6] ?? 0,
  };
}
